import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Table, message, Button, Modal, Select, DatePicker, Row, Col, Space, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { SearchOutlined, PlusOutlined, EditOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PageTitle, StyledCard, ActionButtons } from '../common';
import { theme } from '../../theme';
import { apiClient } from '../../utils/api';



interface Producto {
  id: number;
  descripcion: string;
  codigo?: string;
}

interface Orden {
  id: number;
  nro_orden: number;
  fecha: string;
  producto_nombre: string;
  nproducto_id: number;
  id_contrato: number;
  tipo: string;
  tipo_display: string;
  contrato_comprador_nombre: string;
  contrato_comprador_id: number;
  contrato_vendedor_nombre: string;
  contrato_vendedor_id: number;
  origen_nombre: string;
  origen_id: number;
  empresa_nombre: string;
  empresa_id: number;
  sucursal_nombre: string;
  sucursal_id: number;
  sucursal_otra_nombre: string;
  sucursal_otra_id: number;
  motivo: number;
  descripcion_motivo: string;
  estado: string;
  alta: number;
  cantidad_fardos: number;
  total_kilos: number;
  [key: string]: any;
}

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  title: {
    color: 'white',
    backgroundColor: '#1890ff',
    marginBottom: '20px',
    textAlign: 'center' as const,
    padding: '15px',
    borderRadius: '5px',
    textTransform: 'uppercase' as const,
    fontWeight: 'bold',
    fontSize: '24px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
    marginTop: '20px',
    flexWrap: 'wrap' as const,
  },
  buttonGroup: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap' as const,
  },
  button: {
    backgroundColor: '#87CEEB', // Celeste
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'background-color 0.3s ease',
    padding: '8px 16px',
    minWidth: '140px',
  },
  buttonHover: {
    backgroundColor: '#5F9EA0', // Celeste más oscuro para hover
  },
  buttonDisabled: {
    backgroundColor: '#D3D3D3',
    color: '#808080',
    cursor: 'not-allowed',
  },


  tableContainer: {
    overflowX: 'auto' as const,
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    borderRadius: '5px',
  },
  statusTag: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
  },
  statusActiva: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  statusBaja: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  statusCerrada: {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
  },
  tipoTag: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
  },
  tipoCompra: {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
  },
  tipoVenta: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  tipoOtros: {
    backgroundColor: '#fff3cd',
    color: '#856404',
  },
  filterCard: {
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
  },
  filterRow: {
    marginBottom: '16px',
  },
  searchButton: {
    backgroundColor: '#52c41a',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'background-color 0.3s ease',
    padding: '8px 16px',
    minWidth: '120px',
  },
  searchButtonHover: {
    backgroundColor: '#389e0d',
  },
};

const ListadoOrdenes = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();

  const [ordenes, setOrdenes] = useState<Orden[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  // Ref para cancelar peticiones anteriores
  const abortControllerRef = useRef<AbortController | null>(null);
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    producto_id: undefined as number | undefined,
    estado: undefined as string | undefined,
    tipo: undefined as string | undefined,
    desde_fecha: dayjs().subtract(30, 'day'), // Últimos 30 días
    hasta_fecha: dayjs(), // Fecha actual
  });

  // Cargar productos al inicio
  useEffect(() => {
    const cargarProductos = async () => {
      try {
        setLoadingProductos(true);
        const token = await getAccessTokenSilently();
        const idEmpresa = user?.id_empresa;
        const idSucursal = user?.id_sucursal;
        if (!idEmpresa) {
          message.error('No se encontró el ID de empresa en el usuario');
          return;
        }
        if (!idSucursal) {
          message.error('No se encontró el ID de sucursal en el usuario');
          return;
        }

        // URL para obtener productos
        const url = `${process.env.REACT_APP_BALANZA_URL}/productos/`;


        const response = await apiClient.get<Producto[]>(url, { token });


        setProductos(response.data);
      } catch (error) {
        console.error('Error al cargar productos:', error);
        message.error('Error al cargar los productos. Por favor, intente nuevamente.');
      } finally {
        setLoadingProductos(false);
      }
    };

    cargarProductos();
  }, [getAccessTokenSilently, user]);
  // Función para buscar órdenes con filtros
  const buscarOrdenes = async () => {
    // Cancelar petición anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Crear nuevo controlador
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const idEmpresa = user?.id_empresa;
      const idSucursal = user?.id_sucursal;

      if (!idEmpresa) {
        message.error('No se encontró el ID de empresa en el usuario');
        return;
      }
      if (!idSucursal) {
        message.error('No se encontró el ID de sucursal en el usuario');
        return;
      }

      // Construir URL con filtros
      const params = new URLSearchParams();
      if (filtros.producto_id) {
        params.append('producto_id', filtros.producto_id.toString());
      }
      if (filtros.estado) {
        params.append('estado', filtros.estado);
      }
      if (filtros.tipo) {
        params.append('tipo', filtros.tipo);
      }
      if (filtros.desde_fecha) {
        params.append('desde_fecha', filtros.desde_fecha.format('YYYY-MM-DD'));
      }
      if (filtros.hasta_fecha) {
        params.append('hasta_fecha', filtros.hasta_fecha.format('YYYY-MM-DD'));
      }

      const url = `${process.env.REACT_APP_ORDENES_URL}/listado_ordenes/${idEmpresa}/${idSucursal}/?${params.toString()}`;


      const response = await apiClient.get<Orden[]>(url, {
        token,
        signal: abortController.signal,
        timeout: 60000 // 60 segundos de timeout
      });


      setOrdenes(response.data);
      message.success(`Se encontraron ${response.data.length} órdenes`);
    } catch (error: any) {
      // Si fue cancelado, no hacer nada
      if (error.name === 'CanceledError' || error.message === 'canceled' || axios.isCancel(error)) {
        console.log('Petición cancelada');
        return;
      }

      console.error('Error al buscar órdenes:', error);

      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message.error('La búsqueda está tardando demasiado. Intente reducir el rango de fechas.');
      } else {
        message.error('Error al buscar las órdenes. Por favor, intente nuevamente.');
      }
    } finally {
      // Solo desactivar loading si es la petición actual
      if (abortControllerRef.current === abortController) {
        setLoading(false);
        abortControllerRef.current = null;
      }
    }
  };

  // Función para resetear filtros
  const resetearFiltros = () => {
    setFiltros({
      producto_id: undefined,
      estado: undefined,
      tipo: undefined,
      desde_fecha: dayjs().subtract(30, 'day'),
      hasta_fecha: dayjs(),
    });
    setOrdenes([]);
    message.info('Filtros reseteados. Haga clic en "Buscar" para ver los resultados.');
  };

  const handleOrdenConContrato = () => {
    navigate('/altaordenes');
  };

  const handleOrdenSinContrato = () => {
    navigate('/altasincontratos');
  };

  const handleEditar = (ordenId: number) => {
    navigate(`/ordenes/editar/${ordenId}`);
  };

  const handleEmision = (orden: Orden) => {
    navigate('/emisionordenes', {
      state: {
        id_orden: orden.id.toString(),
        nro_orden: orden.nro_orden,
        id_sucursal: orden.sucursal
      }
    });
  };

  const handleVerClasificacion = (orden: Orden) => {
    navigate('/clasificacionordenes', {
      state: {
        id_orden: orden.id.toString(),
        nro_orden: orden.nro_orden,
        id_sucursal: orden.sucursal
      }
    });
  };

  const getStatusStyle = (estado: string) => {
    switch (estado) {
      case 'Activa':
        return { ...styles.statusTag, ...styles.statusActiva };
      case 'Baja':
        return { ...styles.statusTag, ...styles.statusBaja };
      case 'Cerrada':
        return { ...styles.statusTag, ...styles.statusCerrada };
      default:
        return styles.statusTag;
    }
  };

  const getTipoStyle = (tipo: string) => {
    switch (tipo) {
      case 'C':
        return { ...styles.tipoTag, ...styles.tipoCompra };
      case 'V':
        return { ...styles.tipoTag, ...styles.tipoVenta };
      default:
        return { ...styles.tipoTag, ...styles.tipoOtros };
    }
  };

  const columns: ColumnsType<Orden> = [
    {
      title: 'Nro Orden',
      dataIndex: 'nro_orden',
      key: 'nro_orden',
      width: 100,
      sorter: (a, b) => a.nro_orden - b.nro_orden,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
      sorter: (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_display',
      key: 'tipo_display',
      width: 120,
      render: (_, record) => (
        <span style={getTipoStyle(record.tipo)}>
          {record.tipo_display}
        </span>
      ),
    },
    {
      title: 'Producto',
      dataIndex: 'producto_nombre',
      key: 'producto_nombre',
      width: 150,
    },
    {
      title: 'Proveedor/Deudor',
      dataIndex: 'proveedor_deudor',
      key: 'proveedor_deudor',
      width: 200,
      render: (_, record) => {
        // Para tipo Compra (C): mostrar contrato_vendedor_nombre
        // Para tipo Venta (V): mostrar contrato_comprador_nombre
        if (record.tipo === 'C') {
          return record.contrato_vendedor_nombre || '-';
        } else if (record.tipo === 'V') {
          return record.contrato_comprador_nombre || '-';
        } else {
          // Para otros tipos, mostrar origen_nombre como fallback
          return record.origen_nombre || '-';
        }
      },
    },
    {
      title: 'Fardos',
      dataIndex: 'cantidad_fardos',
      key: 'cantidad_fardos',
      width: 80,
      sorter: (a, b) => a.cantidad_fardos - b.cantidad_fardos,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      render: (estado) => (
        <span style={getStatusStyle(estado)}>
          {estado}
        </span>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 160,
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditar(record.id)}
            />
          </Tooltip>
          <Tooltip title="Emisión">
            <Button
              icon={<FileTextOutlined />}
              size="small"
              onClick={() => handleEmision(record)}
              style={{ backgroundColor: theme.colors.secondary, borderColor: theme.colors.secondary, color: 'white' }}
            />
          </Tooltip>
          <Tooltip title="Ver Clasificación">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleVerClasificacion(record)}
              style={{ backgroundColor: theme.colors.success, borderColor: theme.colors.success, color: 'white' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0' }}>
      <PageTitle>Listado de Órdenes</PageTitle>

      {/* Botones de acciones generales */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '12px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOrdenConContrato}
          size="large"
          style={{ backgroundColor: theme.colors.primary, borderColor: theme.colors.primary }}
        >
          Orden c/Contrato
        </Button>
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={handleOrdenSinContrato}
          size="large"
        >
          Orden s/Contrato
        </Button>
      </div>

      {/* Filtros de búsqueda */}
      <StyledCard title="Filtros de Búsqueda">
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={styles.filterRow}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Producto:
              </label>
              <Select
                placeholder="Seleccionar producto"
                style={{ width: '100%' }}
                value={filtros.producto_id}
                onChange={(value) => setFiltros({ ...filtros, producto_id: value })}
                loading={loadingProductos}
                allowClear
              >
                {productos.map((producto) => (
                  <Select.Option key={producto.id} value={producto.id}>
                    {producto.descripcion}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={styles.filterRow}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Estado:
              </label>
              <Select
                placeholder="Seleccionar estado"
                style={{ width: '100%' }}
                value={filtros.estado}
                onChange={(value) => setFiltros({ ...filtros, estado: value })}
                allowClear
              >
                <Select.Option value="1">1 - Pendiente Recepcion/Despacho</Select.Option>
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={styles.filterRow}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Tipo:
              </label>
              <Select
                placeholder="Seleccionar tipo"
                style={{ width: '100%' }}
                value={filtros.tipo}
                onChange={(value) => setFiltros({ ...filtros, tipo: value })}
                allowClear
              >
                <Select.Option value="C">Compras</Select.Option>
                <Select.Option value="V">Venta</Select.Option>
                <Select.Option value="A">Sucursal {'-->'}</Select.Option>
                <Select.Option value="F">Sucursal {'<--'}</Select.Option>
                <Select.Option value="D">Cambio Dueño {'-->'}</Select.Option>
                <Select.Option value="G">Cambio Dueño</Select.Option>
                <Select.Option value="E">Retiro Clientes</Select.Option>
                <Select.Option value="B">Baja</Select.Option>
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={styles.filterRow}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Desde Fecha:
              </label>
              <DatePicker
                style={{ width: '100%' }}
                value={filtros.desde_fecha}
                onChange={(date) => setFiltros({ ...filtros, desde_fecha: date })}
                format="DD/MM/YYYY"
                placeholder="Desde fecha"
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={styles.filterRow}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Hasta Fecha:
              </label>
              <DatePicker
                style={{ width: '100%' }}
                value={filtros.hasta_fecha}
                onChange={(date) => setFiltros({ ...filtros, hasta_fecha: date })}
                format="DD/MM/YYYY"
                placeholder="Hasta fecha"
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={styles.filterRow}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={buscarOrdenes}
                loading={loading}
                style={styles.searchButton}
              >
                Buscar
              </Button>
            </div>
          </Col>
        </Row>
      </StyledCard>

      {/* Tabla de órdenes */}
      <StyledCard title="Órdenes" bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={ordenes}
          rowKey="id"
          loading={loading}
          locale={{
            emptyText: ordenes.length === 0 && !loading ?
              'No hay órdenes para mostrar. Configure los filtros y haga clic en "Buscar".' :
              'No hay datos'
          }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} órdenes`,
          }}
          scroll={{ x: 1200 }}
          size="middle"
          summary={(pageData) => {
            const totalFardos = pageData.reduce((sum, record) => sum + (record.cantidad_fardos || 0), 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6} align="right">
                    <strong>Total Fardos:</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="center">
                    <strong>{Intl.NumberFormat('es-AR').format(totalFardos)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} colSpan={1} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </StyledCard>
    </div>
  );
};

export default ListadoOrdenes;
